import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Shield, LogOut, Eye, Building2, Search, Loader2, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Support Mode Banner - Shows when in impersonation mode
export const SupportModeBanner = ({ supportInfo, onExit }) => {
  const [exiting, setExiting] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');
  
  useEffect(() => {
    if (!supportInfo?.expires_at) return;
    
    const updateTimer = () => {
      const now = new Date();
      const expires = new Date(supportInfo.expires_at);
      const diff = expires - now;
      
      if (diff <= 0) {
        setTimeLeft('Expirado');
        return;
      }
      
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [supportInfo?.expires_at]);
  
  const handleExit = async () => {
    setExiting(true);
    try {
      const response = await fetch(`${API_URL}/api/support/exit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success('Modo soporte finalizado');
        onExit();
      } else {
        toast.error('Error al salir del modo soporte');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setExiting(false);
    }
  };
  
  return (
    <div 
      className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 px-4 shadow-lg"
      data-testid="support-mode-banner"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5" />
          <span className="font-medium">MODO SOPORTE</span>
          <Badge className="bg-white/20 text-white border-0">
            <Building2 className="w-3 h-3 mr-1" />
            {supportInfo?.institution_name || 'Institución'}
          </Badge>
          <div className="flex items-center gap-1 text-sm opacity-80">
            <Clock className="w-4 h-4" />
            <span>{timeLeft}</span>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleExit}
          disabled={exiting}
          className="text-white hover:bg-white/20"
          data-testid="exit-support-mode-btn"
        >
          {exiting ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <LogOut className="w-4 h-4 mr-2" />
          )}
          Salir
        </Button>
      </div>
    </div>
  );
};


// Support Mode Dialog - For starting impersonation
export const SupportModeDialog = ({ open, onOpenChange, onStartSupport }) => {
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState(60);
  const [starting, setStarting] = useState(false);
  
  useEffect(() => {
    if (open) {
      fetchInstitutions();
    }
  }, [open]);
  
  const fetchInstitutions = async (search = '') => {
    setSearching(true);
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (search) params.append('search', search);
      
      const response = await fetch(`${API_URL}/api/support/institutions-for-support?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInstitutions(data);
      }
    } catch (error) {
      console.error('Error fetching institutions:', error);
    } finally {
      setSearching(false);
    }
  };
  
  const handleSearch = () => {
    fetchInstitutions(searchQuery);
  };
  
  const handleStart = async () => {
    if (!selectedInstitution || !reason.trim()) {
      toast.error('Seleccione una institución y proporcione una razón');
      return;
    }
    
    setStarting(true);
    try {
      const response = await fetch(`${API_URL}/api/support/impersonate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          institution_id: selectedInstitution.id,
          reason: reason.trim(),
          duration_minutes: duration
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Store the support token
        localStorage.setItem('token', data.token);
        localStorage.setItem('support_mode', JSON.stringify({
          institution_id: data.institution_id,
          institution_name: data.institution_name,
          expires_at: data.expires_at
        }));
        
        toast.success(`Modo soporte activado para ${data.institution_name}`);
        onStartSupport(data);
        onOpenChange(false);
        
        // Reload to apply new context
        window.location.reload();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al iniciar modo soporte');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setStarting(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-500" />
            Modo Soporte
          </DialogTitle>
          <DialogDescription>
            Acceda temporalmente a una institución para brindar soporte técnico. 
            Todas las acciones serán auditadas.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Search */}
          <div>
            <Label>Buscar Institución</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Nombre o ID..."
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button variant="outline" onClick={handleSearch} disabled={searching}>
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          
          {/* Institution List */}
          <div className="max-h-48 overflow-y-auto border rounded-lg">
            {institutions.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {searching ? 'Buscando...' : 'No se encontraron instituciones'}
              </div>
            ) : (
              institutions.map((inst) => (
                <div
                  key={inst.id}
                  onClick={() => setSelectedInstitution(inst)}
                  className={`p-3 cursor-pointer border-b last:border-b-0 hover:bg-gray-50 ${
                    selectedInstitution?.id === inst.id ? 'bg-blue-50 border-blue-200' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{inst.nombre}</p>
                      <p className="text-xs text-gray-500">{inst.tipo} • {inst.region}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {inst.user_count} usuarios
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {selectedInstitution && (
            <>
              {/* Reason */}
              <div>
                <Label>Razón del Acceso *</Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Describa la razón del soporte..."
                  rows={2}
                  className="mt-1"
                />
              </div>
              
              {/* Duration */}
              <div>
                <Label>Duración (minutos)</Label>
                <Input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(parseInt(e.target.value) || 60)}
                  min={15}
                  max={480}
                  className="mt-1 w-32"
                />
                <p className="text-xs text-gray-500 mt-1">
                  La sesión expirará automáticamente después de este tiempo
                </p>
              </div>
            </>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleStart} 
            disabled={starting || !selectedInstitution || !reason.trim()}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {starting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Eye className="w-4 h-4 mr-2" />
            Iniciar Soporte
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// Hook to check support mode status
export const useSupportMode = () => {
  const [isSupportMode, setIsSupportMode] = useState(false);
  const [supportInfo, setSupportInfo] = useState(null);
  
  useEffect(() => {
    const checkSupportMode = () => {
      const stored = localStorage.getItem('support_mode');
      if (stored) {
        try {
          const info = JSON.parse(stored);
          const expires = new Date(info.expires_at);
          
          if (expires > new Date()) {
            setIsSupportMode(true);
            setSupportInfo(info);
          } else {
            // Expired
            localStorage.removeItem('support_mode');
            setIsSupportMode(false);
            setSupportInfo(null);
          }
        } catch {
          localStorage.removeItem('support_mode');
        }
      }
    };
    
    checkSupportMode();
    const interval = setInterval(checkSupportMode, 10000); // Check every 10s
    return () => clearInterval(interval);
  }, []);
  
  const exitSupportMode = () => {
    localStorage.removeItem('support_mode');
    localStorage.removeItem('token');
    setIsSupportMode(false);
    setSupportInfo(null);
    window.location.href = '/login';
  };
  
  return { isSupportMode, supportInfo, exitSupportMode };
};

export default SupportModeBanner;
